import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  Radio,
  RadioGroup,
  Slider,
  TextField,
  Typography,
} from '@mui/material'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useSnackbar } from 'hooks/useSnackbar/SnackbarContext'

import OnboardingThankYouMessage from '../../../components/OnboardingThankYouMessage'
import { useOnboarding } from '../insight/onboarding-questionnaire/_context'

interface OnboardingSurveyData {
  // 勤務条件・働き方の希望
  weeklyWorkingDays: number
  idealMonthlySalary: string
  workplacePreference: 'small' | 'large' | 'either'
  shiftTimePreference: 'day' | 'night' | 'either'
  commuteDistance: number
  commuteMethod: string[]

  // キャリア志向
  teamPosition: 'leader' | 'support'
  skillUpInterest: boolean
  qualificationInterest: string[]

  // 会社を知った経路
  companyDiscoverySource: 1 | 2 | 3 | 4 | ''
}

const defaultData: OnboardingSurveyData = {
  weeklyWorkingDays: 3,
  idealMonthlySalary: '',
  workplacePreference: '' as any,
  shiftTimePreference: '' as any,
  commuteDistance: 30,
  commuteMethod: [],
  teamPosition: '' as any,
  skillUpInterest: false,
  qualificationInterest: [],
  companyDiscoverySource: '' as any,
}

const commuteMethodKeys = [
  'train',
  'car',
  'motorcycle',
  'bicycle',
  'walking',
] as const

const commuteMethodBits: Record<string, number> = {
  train: 1,
  car: 2,
  motorcycle: 4,
  bicycle: 8,
  walking: 16,
}

export default function OnboardingQuestionnaireForm() {
  const { t } = useTranslation('page-insight-onboarding')
  const { showSnackbar } = useSnackbar()
  const { questionnaire, updateQuestionnaire, isLoading } = useOnboarding()
  const [formData, setFormData] = useState<OnboardingSurveyData>(defaultData)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // ステータスが2（回答済み）の場合はThankYouメッセージを表示
  if (questionnaire?.status === 2) {
    return <OnboardingThankYouMessage />
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // バリデーション
    if (!formData.idealMonthlySalary) {
      showSnackbar(t('alerts.salary_required'))
      return
    }
    if (!formData.workplacePreference) {
      showSnackbar(t('alerts.workplace_required'))
      return
    }
    if (!formData.shiftTimePreference) {
      showSnackbar(t('alerts.shift_required'))
      return
    }
    if (formData.commuteMethod.length === 0) {
      showSnackbar(t('alerts.commute_required'))
      return
    }
    if (!formData.teamPosition) {
      showSnackbar(t('alerts.career_required'))
      return
    }
    if (!formData.companyDiscoverySource) {
      showSnackbar(t('alerts.discovery_required'))
      return
    }

    setIsSubmitting(true)

    try {
      // フォームデータをAPI用データに変換
      const apiData = {
        weeklyWorkDays: formData.weeklyWorkingDays,
        idealMonthlySalary: formData.idealMonthlySalary
          ? parseInt(formData.idealMonthlySalary)
          : undefined,
        workEnvironmentPreference:
          formData.workplacePreference === 'small'
            ? 1
            : formData.workplacePreference === 'large'
              ? 2
              : (3 as 1 | 2 | 3),
        shiftTimePreference:
          formData.shiftTimePreference === 'day'
            ? 1
            : formData.shiftTimePreference === 'night'
              ? 2
              : (3 as 1 | 2 | 3),
        commuteTimeMinutes: formData.commuteDistance,
        commuteMethods: formData.commuteMethod.reduce(
          (acc, method) => acc | (commuteMethodBits[method] || 0),
          0,
        ),
        careerPositionPreference:
          formData.teamPosition === 'leader' ? 1 : (2 as 1 | 2),
        skillDevelopmentInterest: formData.skillUpInterest,
        companyDiscoverySource: parseInt(
          formData.companyDiscoverySource.toString(),
        ),
      }

      const success = await updateQuestionnaire(apiData)
      if (success) {
        showSnackbar(t('alerts.submit_success'), 'success')
      }
    } catch (err) {
      console.error('送信エラー:', err)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleWeeklyDaysChange = (value: number) => {
    setFormData((prev) => ({ ...prev, weeklyWorkingDays: value }))
  }

  const handleCommuteDistanceChange = (value: number) => {
    setFormData((prev) => ({ ...prev, commuteDistance: value }))
  }

  const handleCommuteMethodChange = (method: string, checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      commuteMethod: checked
        ? [...prev.commuteMethod, method]
        : prev.commuteMethod.filter((m) => m !== method),
    }))
  }

  return (
    <Box sx={{ maxWidth: 500, mx: 'auto', p: 2, fontSize: '20px' }}>
      <Typography
        variant="h4"
        component="h1"
        gutterBottom
        fontWeight="bold"
        sx={{
          fontSize: '28px',
          textAlign: 'center',
          mt: 4,
          mb: 4,
        }}
      >
        {t('title_line1')}
        <br />
        {t('title_line2')}
      </Typography>

      <Typography
        variant="body1"
        color="text.secondary"
        sx={{ mb: 4, fontSize: '18px' }}
      >
        {t('description_line1')}
        <br />
        {t('description_line2')}
      </Typography>

      <form onSubmit={handleSubmit}>
        {/* 勤務条件・働き方の希望 */}
        <Card sx={{ mb: 3, boxShadow: 'none' }}>
          <CardContent>
            {/* 週の稼働日数 */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontSize: '24px' }}>
                {t('questions.weekly_days', {
                  days: formData.weeklyWorkingDays,
                })}
              </Typography>
              <Slider
                value={formData.weeklyWorkingDays}
                onChange={(_, value) => handleWeeklyDaysChange(value as number)}
                min={1}
                max={5}
                marks={[
                  { value: 1, label: '1' },
                  { value: 3, label: '3' },
                  { value: 5, label: '5' },
                ]}
                valueLabelDisplay="auto"
              />
            </Box>

            {/* 理想給与 */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontSize: '24px' }}>
                {t('questions.monthly_salary')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextField
                  fullWidth
                  placeholder="10"
                  value={formData.idealMonthlySalary}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setFormData((prev) => ({
                      ...prev,
                      idealMonthlySalary: e.target.value,
                    }))
                  }
                  size="small"
                  type="number"
                  required
                  sx={{ '& .MuiInputBase-input': { fontSize: '20px' } }}
                />
                <Typography
                  variant="body1"
                  sx={{ fontSize: '20px', whiteSpace: 'nowrap' }}
                >
                  {t('questions.salary_unit')}
                </Typography>
              </Box>
            </Box>

            {/* 現場規模の希望 */}
            <Box sx={{ mb: 3 }}>
              <FormControl component="fieldset">
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 1, fontSize: '24px' }}
                >
                  {t('questions.workplace_scale')}
                </Typography>

                <RadioGroup
                  value={formData.workplacePreference}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      workplacePreference: e.target.value as any,
                    }))
                  }
                >
                  <FormControlLabel
                    value="either"
                    control={<Radio />}
                    label={t('options.either')}
                    sx={{
                      '& .MuiFormControlLabel-label': { fontSize: '20px' },
                    }}
                  />

                  <FormControlLabel
                    value="small"
                    control={<Radio />}
                    label={t('options.small_team')}
                    sx={{
                      '& .MuiFormControlLabel-label': { fontSize: '20px' },
                    }}
                  />
                  <FormControlLabel
                    value="large"
                    control={<Radio />}
                    label={t('options.large_team')}
                    sx={{
                      '& .MuiFormControlLabel-label': { fontSize: '20px' },
                    }}
                  />
                </RadioGroup>
              </FormControl>
            </Box>

            {/* シフト時間帯 */}
            <Box sx={{ mb: 3 }}>
              <FormControl component="fieldset">
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 1, fontSize: '24px' }}
                >
                  {t('questions.shift_time')}
                </Typography>
                <RadioGroup
                  value={formData.shiftTimePreference}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      shiftTimePreference: e.target.value as any,
                    }))
                  }
                >
                  <FormControlLabel
                    value="either"
                    control={<Radio />}
                    label={t('options.either')}
                    sx={{
                      '& .MuiFormControlLabel-label': { fontSize: '20px' },
                    }}
                  />

                  <FormControlLabel
                    value="day"
                    control={<Radio />}
                    label={t('options.day_shift')}
                    sx={{
                      '& .MuiFormControlLabel-label': { fontSize: '20px' },
                    }}
                  />
                  <FormControlLabel
                    value="night"
                    control={<Radio />}
                    label={t('options.night_shift')}
                    sx={{
                      '& .MuiFormControlLabel-label': { fontSize: '20px' },
                    }}
                  />
                </RadioGroup>
              </FormControl>
            </Box>

            {/* 通勤距離 */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontSize: '24px' }}>
                {t('questions.commute_distance', {
                  distance: formData.commuteDistance,
                })}
              </Typography>
              <Slider
                value={formData.commuteDistance}
                onChange={(_, value) =>
                  handleCommuteDistanceChange(value as number)
                }
                min={10}
                max={90}
                step={5}
                marks={[
                  { value: 10, label: t('slider_marks.minutes_10') },
                  { value: 30, label: t('slider_marks.minutes_30') },
                  { value: 60, label: t('slider_marks.minutes_60') },
                  { value: 90, label: t('slider_marks.minutes_90') },
                ]}
                valueLabelDisplay="auto"
              />
            </Box>

            {/* 通勤手段 */}
            <Box sx={{ mb: 3 }}>
              <FormControl component="fieldset">
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 1, fontSize: '24px' }}
                >
                  {t('questions.commute_method')}
                </Typography>
                <FormGroup>
                  {commuteMethodKeys.map((key) => (
                    <FormControlLabel
                      key={key}
                      control={
                        <Checkbox
                          checked={formData.commuteMethod.includes(key)}
                          onChange={(e) =>
                            handleCommuteMethodChange(key, e.target.checked)
                          }
                        />
                      }
                      label={t(`commute_methods.${key}`)}
                      sx={{
                        '& .MuiFormControlLabel-label': { fontSize: '20px' },
                      }}
                    />
                  ))}
                </FormGroup>
              </FormControl>
            </Box>

            <Typography variant="subtitle2" sx={{ mb: 1, fontSize: '24px' }}>
              {t('questions.career')}
            </Typography>

            {/* チーム内ポジション */}
            <Box sx={{ mb: 3 }}>
              <FormControl component="fieldset">
                <RadioGroup
                  value={formData.teamPosition}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      teamPosition: e.target.value as any,
                    }))
                  }
                >
                  <FormControlLabel
                    value="leader"
                    control={<Radio />}
                    label={t('options.leader')}
                    sx={{
                      '& .MuiFormControlLabel-label': { fontSize: '20px' },
                    }}
                  />
                  <FormControlLabel
                    value="support"
                    control={<Radio />}
                    label={t('options.not_interested')}
                    sx={{
                      '& .MuiFormControlLabel-label': { fontSize: '20px' },
                    }}
                  />
                </RadioGroup>
              </FormControl>
            </Box>

            {/* スキルアップ意欲 */}
            <Box sx={{ mb: 3 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.skillUpInterest}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        skillUpInterest: e.target.checked,
                      }))
                    }
                  />
                }
                label={t('options.skill_up')}
                sx={{ '& .MuiFormControlLabel-label': { fontSize: '20px' } }}
              />
            </Box>

            <Typography variant="subtitle2" sx={{ mb: 1, fontSize: '24px' }}>
              {t('questions.discovery_source')}
            </Typography>

            {/* 会社を知った経路 */}
            <Box sx={{ mb: 3 }}>
              <FormControl component="fieldset">
                <RadioGroup
                  value={formData.companyDiscoverySource}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      companyDiscoverySource: e.target.value as any,
                    }))
                  }
                >
                  <FormControlLabel
                    value={1}
                    control={<Radio />}
                    label={t('options.job_site')}
                    sx={{
                      '& .MuiFormControlLabel-label': { fontSize: '20px' },
                    }}
                  />
                  <FormControlLabel
                    value={2}
                    control={<Radio />}
                    label={t('options.newspaper')}
                    sx={{
                      '& .MuiFormControlLabel-label': { fontSize: '20px' },
                    }}
                  />
                  <FormControlLabel
                    value={3}
                    control={<Radio />}
                    label={t('options.referral')}
                    sx={{
                      '& .MuiFormControlLabel-label': { fontSize: '20px' },
                    }}
                  />
                  <FormControlLabel
                    value={4}
                    control={<Radio />}
                    label={t('options.other')}
                    sx={{
                      '& .MuiFormControlLabel-label': { fontSize: '20px' },
                    }}
                  />
                </RadioGroup>
              </FormControl>
            </Box>
          </CardContent>
        </Card>
        {/* 送信ボタン */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
          <Button
            type="submit"
            variant="contained"
            sx={{ minWidth: 120, fontSize: '18px' }}
            disabled={isSubmitting || isLoading}
          >
            {isSubmitting ? t('submitting') : t('submit')}
          </Button>
        </Box>
      </form>
    </Box>
  )
}
